import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, CheckCircle, ShieldCheck } from 'lucide-react';

// Dynamic injection script builder for loading the secure Razorpay Checkout SDK
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

function Checkout({ accessToken, user, onOrderSuccess, refreshCart }) {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [saveThisAddress, setSaveThisAddress] = useState(false);

  const [delivery, setDelivery] = useState(0);
  const [shippingMessage, setShippingMessage] = useState("Calculating...");

  const [formData, setFormData] = useState({
    pickup_location: "delhi",
    billing_address: "",
    billing_address_2: "",
    billing_city: "",
    billing_pincode: "",
    billing_state: "",
    billing_phone: "",
    payment_method: "Prepaid"
  });

  // Fetch Cart and Saved Addresses together
  useEffect(() => {
    if (!accessToken) {
      navigate('/login');
      return;
    }

    const fetchCart = fetch(`${process.env.REACT_APP_API_URL}/api/products/cart/`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }).then(res => res.json());

    const fetchAddresses = fetch(`${process.env.REACT_APP_API_URL}/api/products/addresses/`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    }).then(res => res.json());

    Promise.all([fetchCart, fetchAddresses])
      .then(([cartData, addressData]) => {
        setCartItems(Array.isArray(cartData) ? cartData : (cartData?.results || []));
        const addressList = Array.isArray(addressData) ? addressData : [];
        setSavedAddresses(addressList);

        // Auto-select default address if it exists
        const defaultAddr = addressList.find(a => a.is_default);
        if (defaultAddr) {
          applySavedAddress(defaultAddr);
        } else if (addressList.length > 0) {
          applySavedAddress(addressList[0]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });
  }, [accessToken, navigate]);

  // Re-fetch live Shiprocket shipping charge quote whenever payment method or pincode changes
  useEffect(() => {
    if (cartItems.length > 0) {
      fetchShippingCost();
    }
  }, [formData.payment_method]);

  // Fetches live quote directly from Django REST backend using GET with query parameters
  const fetchShippingCost = () => {
    setShippingMessage("Recalculating...");

    // Send `cod=1` for COD and `cod=0` for Prepaid
    const codValue = formData.payment_method === 'COD' ? 1 : 0;
    
    const params = new URLSearchParams({
      cod: codValue,
      ...(selectedAddressId && selectedAddressId !== 'new' ? { address_id: selectedAddressId } : {}),
      ...(formData.billing_pincode ? { pincode: formData.billing_pincode } : {})
    });

    fetch(`${process.env.REACT_APP_API_URL}/api/products/cart/shipping-rate/?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`
      }
    })
      .then(res => res.json())
      .then(data => {
        // Unpack nested response: data -> shipping_charge -> shipping_charge
        const chargeData = data?.shipping_charge;

        if (chargeData && typeof chargeData.shipping_charge === 'number') {
          setDelivery(chargeData.shipping_charge);
        } else if (data?.message === "No default address found" || chargeData?.status === "missing_pincode") {
          setDelivery(0);
          setShippingMessage("Add delivery location details below");
        } else {
          setDelivery(0);
          setShippingMessage("Calculated on submission");
        }
      })
      .catch((err) => {
        console.error("Shipping rate calculation error:", err);
        setDelivery(0);
        setShippingMessage("Calculated on submission");
      });
  };

  // Real-time Pincode Lookup for Form Inputs
  useEffect(() => {
    const pincode = formData.billing_pincode.trim();
    if (/^\d{6}$/.test(pincode)) {
      setPincodeLoading(true);
      fetch(`https://api.postalpincode.in/pincode/${pincode}`)
        .then(res => res.json())
        .then(data => {
          if (data && data[0]?.Status === "Success") {
            const details = data[0].PostOffice[0];
            setFormData(prev => ({
              ...prev,
              billing_city: details.District,
              billing_state: details.State
            }));

            setTimeout(() => { fetchShippingCost(); }, 200);
          }
        })
        .catch(err => console.error("Pincode API Error:", err))
        .finally(() => setPincodeLoading(false));
    }
  }, [formData.billing_pincode]);

  const applySavedAddress = (addr) => {
    setSelectedAddressId(addr.id);
    setFormData(prev => ({
      ...prev,
      billing_address: addr.address_line_1,
      billing_address_2: addr.address_line_2 || "",
      billing_city: addr.city,
      billing_state: addr.state,
      billing_pincode: addr.pincode,
      billing_phone: addr.phone_number
    }));

    setTimeout(() => { fetchShippingCost(); }, 200);
  };

  const handleSelectSaved = (addr) => {
    applySavedAddress(addr);
  };

  const handleCreateNewClick = () => {
    setSelectedAddressId('new');
    setDelivery(0);
    setShippingMessage("Waiting for valid pincode input...");
    setFormData(prev => ({
      ...prev,
      billing_address: "",
      billing_address_2: "",
      billing_city: "",
      billing_pincode: "",
      billing_state: "",
      billing_phone: ""
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- Financial & GST Calculations ---
  const subtotalPrice = cartItems.reduce((sum, item) => sum + (item.total_price || 0), 0);

  const totalGstAmount = cartItems.reduce((sum, item) => {
    if (item.gst_amount !== undefined) return sum + item.gst_amount;
    const itemPrice = item.total_price || 0;
    const rate = item.gst_rate !== undefined ? item.gst_rate : 18;
    return sum + (itemPrice * (rate / 100));
  }, 0);

  const finalAmount = subtotalPrice + totalGstAmount + delivery;

  // Reusable post-checkout state cleanup helper 
  const completeCheckoutLifecycle = () => {
    setCartItems([]);
    if (onOrderSuccess) onOrderSuccess();
    if (refreshCart) refreshCart();
    navigate('/');
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    if (!formData.billing_address || !formData.billing_city || !formData.billing_pincode || !formData.billing_phone) {
      alert("Please complete delivery settings fully.");
      return;
    }

    let finalAddressId = selectedAddressId;

    if (selectedAddressId === 'new' && saveThisAddress) {
      try {
        const addressResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/products/addresses/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            phone_number: formData.billing_phone,
            address_line_1: formData.billing_address,
            address_line_2: formData.billing_address_2,
            city: formData.billing_city,
            state: formData.billing_state,
            pincode: formData.billing_pincode,
            is_default: true
          })
        });

        if (addressResponse.ok) {
          const savedAddrData = await addressResponse.json();
          finalAddressId = savedAddrData.id;
        }
      } catch (err) {
        console.error("Address pre-save execution caught error: ", err);
      }
    }

    const explicitPayload = {
      ...formData,
      address_id: finalAddressId,
      subtotal_amount: subtotalPrice,
      gst_amount: totalGstAmount,
      shipping_charges: delivery,
      total_amount: finalAmount
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/checkout/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(explicitPayload)
      });

      const orderResult = await response.json();

      if (!response.ok) {
        alert(orderResult.error || 'Initialization step dropped. Check configuration.');
        return;
      }

      if (orderResult.requires_payment) {
        const isScriptLoaded = await loadRazorpayScript();

        if (!isScriptLoaded) {
          alert('Failed to connect to the payment gateway infrastructure. Check your network.');
          return;
        }

        const paymentOptions = {
          key: orderResult.key_id,
          amount: orderResult.amount,
          currency: orderResult.currency,
          name: "Mridanga Store",
          description: "Secure Order Fulfillment Checkout",
          order_id: orderResult.razorpay_order_id,
          handler: async function (paymentResponse) {
            try {
              const verificationPayload = {
                ...formData,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              };
              const verificationCall = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/checkout/verify/`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify(verificationPayload)
              });

              if (verificationCall.ok) {
                alert('🎉 Payment verified & Order executed via Shiprocket!');
                completeCheckoutLifecycle();
              } else {
                alert('Verification pipeline passed payment capture but logistics linking failed.');
              }
            } catch (err) {
              console.error("Payment confirmation handling failed: ", err);
            }
          },
          prefill: {
            name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || "Customer",
            email: user?.email || "",
            contact: formData.billing_phone
          },
          theme: {
            color: "#9A7B56"
          }
        };

        const razorpayInstance = new window.Razorpay(paymentOptions);
        razorpayInstance.open();

      } else {
        alert('🎉 COD Order executed through Shiprocket successfully!');
        completeCheckoutLifecycle();
      }

    } catch (error) {
      console.error('Order creation processing error:', error);
      alert('An unexpected infrastructure communication failure occurred.');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-32 text-[#A6A29A] uppercase tracking-widest text-[11px] font-semibold bg-[#F2F1EC] min-h-screen flex items-center justify-center">
        Syncing checkout system flow...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F1EC] py-6 px-4">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left Side: Layout Form Blocks */}
        <div className="lg:col-span-2 space-y-6">
          <div className="p-6 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md shadow-xs flex items-center gap-3">
            <ShieldCheck className="w-4 h-4 text-[#9A7B56]" />
            <div>
              <h2 className="text-[#2C2A27] text-sm uppercase tracking-widest font-semibold text-[#9A7B56]">Express Checkout</h2>
              <p className="text-xs text-[#8A867E] font-light mt-0.5">Secure transport encryption protocols enabled</p>
            </div>
          </div>

          {/* Address Selector Module */}
          <div className="bg-[#FDFDFB] p-6 border border-[#E1DFD7] rounded-md space-y-4 shadow-xs">
            <h3 className="text-[10px] font-bold text-[#A6A29A] uppercase tracking-widest mb-2 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> 1. Delivery Configurations
            </h3>

            {/* Render List of Saved Elements */}
            <div className="grid grid-cols-1 gap-3">
              {savedAddresses.map((addr) => (
                <div
                  key={addr.id}
                  onClick={() => handleSelectSaved(addr)}
                  className={`p-4 border rounded-md cursor-pointer transition-all flex items-start gap-3 relative ${selectedAddressId === addr.id ? 'bg-[#FDFDFB] border-[#9A7B56] shadow-xs' : 'bg-[#FDFDFB] border-[#E1DFD7] hover:border-[#9A7B56]/50'}`}
                >
                  <div className="mt-0.5">
                    {selectedAddressId === addr.id ? (
                      <CheckCircle className="w-4 h-4 text-[#9A7B56] fill-[#9A7B56]/10" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-[#E1DFD7] bg-[#F2F1EC]" />
                    )}
                  </div>
                  <div className="text-xs space-y-1 pr-12">
                    <p className="font-semibold text-[#2C2A27] uppercase tracking-wide flex items-center gap-2">
                      Shipping Address {addr.is_default && <span className="text-[9px] bg-[#9A7B56]/10 text-[#9A7B56] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-widest">Default</span>}
                    </p>
                    <p className="text-[#5A5750] font-light leading-relaxed">{addr.address_line_1}{addr.address_line_2 ? `, ${addr.address_line_2}` : ''}</p>
                    <p className="text-[#8A867E]">{addr.city}, {addr.state} - <span className="font-mono font-medium text-[#2C2A27] tracking-wide">{addr.pincode}</span></p>
                    <p className="text-[10px] text-[#A6A29A] pt-0.5 uppercase tracking-wider"><span className="text-[#8A867E]">Phone:</span> {addr.phone_number}</p>
                  </div>
                </div>
              ))}

              {/* Add New Switch Card */}
              <button
                type="button"
                onClick={handleCreateNewClick}
                className={`p-4 border border-dashed rounded-md text-left transition-all flex items-center gap-3 text-[11px] font-semibold uppercase tracking-widest ${selectedAddressId === 'new' ? 'bg-[#F2F1EC] border-[#9A7B56] text-[#9A7B56]' : 'bg-transparent border-[#E1DFD7] text-[#A6A29A] hover:border-[#9A7B56] hover:text-[#9A7B56]'}`}
              >
                <Plus className="w-4 h-4" />
                <span>Deliver to a new location address</span>
              </button>
            </div>

            {/* Dynamic Form Display Box */}
            {selectedAddressId === 'new' && (
              <div className="pt-5 border-t border-[#E1DFD7] space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Mobile Contact Phone *</label>
                    <input type="tel" name="billing_phone" required value={formData.billing_phone} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2.5 text-[#2C2A27] text-xs focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="e.g. 9876543210" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Pincode *</label>
                    <div className="relative">
                      <input type="text" name="billing_pincode" required value={formData.billing_pincode} onChange={handleInputChange} maxLength="6" className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2.5 text-[#2C2A27] text-xs focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="6-digit Area PIN" />
                      {pincodeLoading && <span className="absolute right-3 top-3 text-[9px] text-[#9A7B56] animate-pulse font-medium tracking-wide">Syncing...</span>}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Address Flat/House details *</label>
                  <input type="text" name="billing_address" required value={formData.billing_address} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2.5 text-[#2C2A27] text-xs focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="Flat No, Building Name, Local Street Sector details" />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">Landmark/Locality (Optional)</label>
                  <input type="text" name="billing_address_2" value={formData.billing_address_2} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2.5 text-[#2C2A27] text-xs focus:outline-none focus:border-[#9A7B56] transition-colors" placeholder="Near landmarks, spaces etc." />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">City/Town *</label>
                    <input type="text" name="billing_city" required value={formData.billing_city} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2.5 text-[#2C2A27] text-xs focus:outline-none focus:border-[#9A7B56] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-[#8A867E] uppercase tracking-wider mb-1">State *</label>
                    <input type="text" name="billing_state" required value={formData.billing_state} onChange={handleInputChange} className="w-full bg-[#FDFDFB] border border-[#E1DFD7] rounded-sm p-2.5 text-[#2C2A27] text-xs focus:outline-none focus:border-[#9A7B56] transition-colors" />
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer pt-2 select-none">
                  <input
                    type="checkbox"
                    checked={saveThisAddress}
                    onChange={(e) => setSaveThisAddress(e.target.checked)}
                    className="accent-[#9A7B56] rounded-sm h-3.5 w-3.5 border-[#E1DFD7]"
                  />
                  <span className="text-xs text-[#8A867E] font-light">Save this destination profile address for future orders</span>
                </label>
              </div>
            )}
          </div>

          <form onSubmit={handlePlaceOrder} className="space-y-6">
            {/* Payment Options Panel */}
            <div className="bg-[#FDFDFB] p-6 border border-[#E1DFD7] space-y-4 rounded-md shadow-xs">
              <h3 className="text-[10px] font-bold text-[#A6A29A] uppercase tracking-widest mb-2">
                2. Method of Payment
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className={`flex items-center gap-3 p-4 rounded-md border cursor-pointer transition-all ${formData.payment_method === 'Prepaid' ? 'bg-[#F2F1EC]/50 border-[#9A7B56] shadow-xs' : 'bg-[#FDFDFB] border-[#E1DFD7] hover:border-[#9A7B56]/50'}`}>
                  <input type="radio" name="payment_method" value="Prepaid" checked={formData.payment_method === 'Prepaid'} onChange={handleInputChange} className="accent-[#9A7B56] h-4 w-4" />
                  <div className="text-xs">
                    <p className="font-semibold uppercase tracking-wide text-[#2C2A27]">Prepaid Secure Gateway</p>
                    <p className="text-[11px] text-[#8A867E] font-light mt-0.5">Cards, UPI, Netbanking pipelines</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-4 rounded-md border cursor-pointer transition-all ${formData.payment_method === 'COD' ? 'bg-[#F2F1EC]/50 border-[#9A7B56] shadow-xs' : 'bg-[#FDFDFB] border-[#E1DFD7] hover:border-[#9A7B56]/50'}`}>
                  <input type="radio" name="payment_method" value="COD" checked={formData.payment_method === 'COD'} onChange={handleInputChange} className="accent-[#9A7B56] h-4 w-4" />
                  <div className="text-xs">
                    <p className="font-semibold uppercase tracking-wide text-[#2C2A27]">Cash On Delivery (COD)</p>
                    <p className="text-[11px] text-[#8A867E] font-light mt-0.5">Handover currency on delivery</p>
                  </div>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-[#F2F1EC] hover:bg-[#9A7B56] text-[#5A5750] hover:text-white border border-[#E1DFD7] hover:border-[#9A7B56] font-semibold py-3.5 rounded-sm transition-all duration-300 uppercase tracking-widest text-[11px]"
            >
              Dispatch Order • Total Value ₹{finalAmount.toFixed(2)}
            </button>
          </form>
        </div>

        {/* Summary Sidebar */}
        <div className="bg-[#FDFDFB] p-6 border border-[#E1DFD7] rounded-md h-fit space-y-4 sticky top-24 shadow-xs">
          <h3 className="text-[10px] font-bold tracking-widest uppercase border-b border-[#E1DFD7] pb-3 text-[#A6A29A]">Items Summary</h3>
          <div className="divide-y divide-[#E1DFD7] max-h-64 overflow-y-auto pr-1">
            {cartItems.map((item, key) => (
              <div key={key} className="py-3 flex justify-between items-center text-xs">
                <div className="max-w-[75%] space-y-0.5">
                  <p className="font-medium text-[#2C2A27] truncate uppercase tracking-wide text-[11px]">{item.product_detail?.product_name || 'Instrument configuration'}</p>
                  <p className="text-[11px] text-[#8A867E] font-light">Quantity: {item.quantity || 1}</p>
                </div>
                <p className="font-semibold text-[#2C2A27]">₹{(item.total_price || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>

          <div className="border-t border-[#E1DFD7] pt-4 space-y-3 text-xs text-[#5A5750]">
            <div className="flex justify-between">
              <span className="font-light">Subtotal (Excl. Tax)</span>
              <span className="text-[#2C2A27] font-medium">₹{subtotalPrice.toFixed(2)}</span>
            </div>

            <div className="flex justify-between">
              <span className="font-light">Estimated GST (18%)</span>
              <span className="text-[#2C2A27] font-medium">₹{totalGstAmount.toFixed(2)}</span>
            </div>

            <div className="flex justify-between">
              <span className="font-light">Delivery Surcharge</span>
              <span className={`font-medium ${delivery === 0 ? "text-[10px] uppercase tracking-wider text-[#8A867E]" : "text-[#2C2A27]"}`}>
                {delivery === 0 ? shippingMessage : `₹${delivery.toFixed(2)}`}
              </span>
            </div>

            <div className="border-t border-dashed border-[#E1DFD7] my-2"></div>

            <div className="flex justify-between text-sm font-semibold text-[#2C2A27] pt-1">
              <span>Total Payable</span>
              <span className="text-base font-bold">₹{finalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Checkout;